// src/components/Sections/UserRoutes/UsuarioRutas.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useStore from "../../../useStore";
import Loader from "../../Elements/Loader/Loader";
import { useToast } from "../../../Contexts/Toast/toast.provider";
import MapaRutaModal from "../../Elements/Modals/MapModal/MapaRutaModal";
import FuelLoadModal from "../../Elements/Modals/FuelLoadModal/FuelLoadModal";
import Button from '../../Elements/Button/Button';
import { FormChangePasswordSchema } from "../../../Models/Schemas/FormChangePasswordSchema";
import { FORM_CHANGE_PASSWORD } from "../../../Constants/ChangePasswordForm";
import useForm from "../../../Hooks/useForm";
import { setErrorFormSimpleUtil } from "../../../Utilities/Forms/errors";
import Input from "../../Elements/Input/Input";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import "./userRoutes.css";
import { es } from "date-fns/locale";

// Helper para obtener username del token
const getLoggedUsername = () => {
  try {
    const raw = localStorage.getItem("logindata");
    if (!raw) return null;
    const { data: { token } = {} } = JSON.parse(raw);
    if (!token) return null;
    const [, payload] = token.split(".");
    const { username } = JSON.parse(atob(payload));
    return username;
  } catch {
    return null;
  }
};

// Convierte "YYYY-MM-DD" a Date local sin desface
const parseYMDToLocalDate = (ymd) => {
  if (!ymd) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
};



// Convierte Date a "YYYY-MM-DD"
const formatLocalDateToYMD = (date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const UsuarioRutas = () => {
  const { username } = useParams();
  const {
    userRoutes,
    routeDays = [], // defensiva: si viene undefined cae a []
    users,
    fetchUsersData,
    error,
    fetchUserRoutesData,
    resetError,
    fetchChangePasswordData,
    fetchPasswordHistoryData,
    clearUserRoutes,
    routesLoading,
    loading,
  } = useStore();

  const {
    values, errors, touched, handleBlur, handleChange, isValid, resetForm
  } = useForm(FORM_CHANGE_PASSWORD, FormChangePasswordSchema);

  const showToast = useToast();
  const navigate = useNavigate();
  const loggedUser = getLoggedUsername();

  // Estados locales
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedFuelLoads, setSelectedFuelLoads] = useState(null);
  const [showFuelModal, setShowFuelModal] = useState(false);
  const [showPwdModal, setShowPwdModal] = useState(false);
  const today = new Date();
  const [searchDate, setSearchDate] = useState(() => formatLocalDateToYMD(today));
  const [historyData, setHistoryData] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [hasHistory, setHasHistory] = useState(false);
  const [displayedMonth, setDisplayedMonth] = useState(() =>
    parseYMDToLocalDate(searchDate)
  );

  useEffect(() => {
    if (searchDate) {
      const d = parseYMDToLocalDate(searchDate);
      if (d) setDisplayedMonth(d);
    }
  }, [searchDate]);


  // 1) Cargar usuarios
  useEffect(() => {
    if (!users.length) {
      fetchUsersData();
    }
  }, [users, fetchUsersData]);

  // 2) Validar username
  useEffect(() => {
    if (!routesLoading && users.length) {
      const exists = users.some((u) => u.username === username);
      if (!exists) {
        navigate("/404", { replace: true });
      }
    }
  }, [routesLoading, users, username, navigate]);

  // 3) Fetch rutas y routeDays cuando cambie username o fecha
  useEffect(() => {
    if (users.some((u) => u.username === username)) {
      clearUserRoutes();
      if (searchDate) {
        fetchUserRoutesData(username, searchDate);
      }
    }
  }, [username, users, searchDate, clearUserRoutes, fetchUserRoutesData]);

  // 4) Manejo de errores
  useEffect(() => {
    if (error) {
      showToast("error", error.message || "Error al cargar datos");
      resetError();
    }
  }, [error, showToast, resetError]);

  // 5) Historial para admin
  useEffect(() => {
    if (loggedUser !== "Administrador") {
      setHasHistory(false);
      return;
    }
    fetchPasswordHistoryData(username)
      .then((history) => {
        setHasHistory(Array.isArray(history) && history.length > 0);
      })
      .catch((err) => {
        showToast("error", err.message || "Error al revisar historial");
        setHasHistory(false);
      });
  }, [loggedUser, username, fetchPasswordHistoryData, showToast]);

  if (!users.some((u) => u.username === username)) return null;
  if (routesLoading) return <Loader />;
  if (loading) return <Loader />;

  const formatDate = (ts) => (ts ? new Date(ts).toLocaleString() : "-");

  const handleChangePassword = async () => {
    try {
      await FormChangePasswordSchema.validate(values, { abortEarly: false });
    } catch (validationError) {
      const message = validationError.errors?.[0] || "Datos inválidos";
      showToast("error", message);
      return;
    }
    try {
      const { currentPassword, newPassword } = values;
      await fetchChangePasswordData(username, currentPassword, newPassword);
      showToast("success", "Contraseña actualizada");
      setShowPwdModal(false);
      resetForm();
    } catch (e) {
      showToast("error", e.message);
    }
  };

  const handleShowHistory = async () => {
    try {
      const history = await fetchPasswordHistoryData(username);
      setHistoryData(history);
      setShowHistoryModal(true);
    } catch (e) {
      showToast("error", e.message);
    }
  };

  // Preparar set de routeDays para comparación rápida
  const routeDaysSet = new Set(routeDays);

  // DEBUG: puedes quitar luego
  // console.debug("routeDays:", routeDays);
  // console.debug("routeDaysSet.has(searchDate):", routeDaysSet.has(searchDate));

  return (
    <div className="container container-home py-4">
      <h2>Rutas de {username}</h2>

      <div className="d-flex gap-3 flex-wrap mb-3">
        <Button
          text="Cambiar contraseña"
          color="gray"
          fill
          action={() => setShowPwdModal(true)}
        />



        {loggedUser === "Administrador" && hasHistory && (
          <Button
            text="Ver historial de contraseñas"
            color="purple"
            fill
            action={handleShowHistory}
            className="mb-3"
          />
        )}
      </div>

      {/* Calendario con indicadores */}
      <div style={{ minWidth: 280 }}>
        <label className="form-label">Fecha de inicio</label>
        <DayPicker
          locale={es}
          mode="single"
          selected={searchDate ? parseYMDToLocalDate(searchDate) : undefined}
          onSelect={(d) => {
            if (d) {
              const iso = formatLocalDateToYMD(d);
              setSearchDate(iso);
            }
          }}
          modifiers={{
            hasRoute: (date) => {
              const key = formatLocalDateToYMD(date);
              return routeDaysSet.has(key);
            },
          }}
          modifiersClassNames={{
            hasRoute: "has-route",
          }}
          month={displayedMonth}
          onMonthChange={setDisplayedMonth}
        />

        <div className="d-flex gap-1">
          <Button
            text="Hoy"
            color="gray"
            fill
            action={() => {
              const isoToday = formatLocalDateToYMD(new Date());
              setSearchDate(isoToday);
              setDisplayedMonth(new Date());
            }}
            className="btn-sm"
          />

          {searchDate && (
            <Button
              text="Limpiar"
              color="gray"
              fill
              action={() => setSearchDate("")}
              className="btn-sm"
            />
          )}
        </div>
      </div>

      {/* Tabla de rutas */}
      {userRoutes.length > 0 ? (
        <>
          <table className="table mt-3">
            <thead>
              <tr>
                <th className="d-none d-md-table-cell">Route ID</th>
                <th className="d-none d-md-table-cell">Unidad</th>
                <th>Inicio</th>
                <th className="d-none d-md-table-cell">Fin</th>
                <th className="d-none d-md-table-cell">Distancia (km)</th>
                <th>Ruta</th>
                <th>Datos Gasolina</th>
              </tr>
            </thead>
            <tbody>
              {userRoutes.map((ruta) => (
                <tr key={ruta.routeId}>
                  <td className="d-none d-md-table-cell">{ruta.routeId}</td>
                  <td className="d-none d-md-table-cell">{ruta.kmUnit}</td>
                  <td>{formatDate(ruta.startTime)}</td>
                  <td className="d-none d-md-table-cell">
                    {ruta.isTracking ? (
                      <span className="text-warning">En ruta</span>
                    ) : (
                      formatDate(ruta.endTime)
                    )}
                  </td>
                  <td className="d-none d-md-table-cell">
                    {(ruta.distanceKm ?? 0).toFixed(2)} km
                  </td>
                  <td>
                    <Button
                      text="Ver ruta"
                      color="green"
                      fill
                      action={() => {
                        setSelectedRoute(ruta);
                        setShowModal(true);
                      }}
                    />
                  </td>
                  <td>
                    {ruta.fuelLoads?.length > 0 ? (
                      <Button
                        text="Ver datos gasolina"
                        color="warning"
                        fill
                        action={() => {
                          setSelectedRoute(ruta);
                          setSelectedFuelLoads(ruta.fuelLoads);
                          setShowFuelModal(true);
                        }}
                      />
                    ) : (
                      <span className="px-3 py-1">No se cargó gasolina</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : (
        <p>No se encontraron rutas para este usuario.</p>
      )}

      {/* Modal historial */}
      {showHistoryModal && (
        <div className="modal-backdrop">
          <div className="modal-content p-4" style={{ maxWidth: 500 }}>
            <h3>Historial de contraseña de {username}</h3>
            {historyData.length > 0 ? (
              <ul className="list-group">
                {historyData.map((entry, idx) => (
                  <li
                    key={idx}
                    className="list-group-item d-flex justify-content-between"
                  >
                    <span>{entry.name}</span>
                    <small>{new Date(entry.time).toLocaleString()}</small>
                  </li>
                ))}
              </ul>
            ) : (
              <p>No hay historial registrado.</p>
            )}
            <div className="text-end mt-3">
              <Button
                text="Cerrar"
                color="gray"
                fill={false}
                action={() => setShowHistoryModal(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal cambiar contraseña */}
      {showPwdModal && (
        <div className="modal-backdrop">
          <div className="modal-content p-4">
            <h3>Cambiar contraseña de {username}</h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleChangePassword();
              }}
            >
              <Input
                variant="Password"
                name="currentPassword"
                label="Contraseña actual"
                placeholder="Ingresa tu contraseña actual"
                value={values.currentPassword}
                onChange={handleChange("currentPassword")}
                onBlur={handleBlur("currentPassword")}
                error={setErrorFormSimpleUtil(
                  errors,
                  touched,
                  "currentPassword"
                )}
                success={touched.currentPassword && !errors.currentPassword}
              />
              <Input
                variant="Password"
                name="newPassword"
                label="Nueva contraseña"
                placeholder="Ingresa la nueva contraseña"
                value={values.newPassword}
                onChange={handleChange("newPassword")}
                onBlur={handleBlur("newPassword")}
                error={setErrorFormSimpleUtil(errors, touched, "newPassword")}
                success={touched.newPassword && !errors.newPassword}
              />
              <Input
                variant="Password"
                name="confirmPassword"
                label="Confirmar contraseña"
                placeholder="Repite la nueva contraseña"
                value={values.confirmPassword}
                onChange={handleChange("confirmPassword")}
                onBlur={handleBlur("confirmPassword")}
                error={setErrorFormSimpleUtil(
                  errors,
                  touched,
                  "confirmPassword"
                )}
                success={touched.confirmPassword && !errors.confirmPassword}
              />
              <div className="d-flex justify-content-end mt-3">
                <Button
                  text="Cancelar"
                  color="red"
                  fill
                  action={() => {
                    setShowPwdModal(false);
                  }}
                  className="me-2"
                />
                <Button
                  type="submit"
                  text="Guardar"
                  color="green"
                  fill
                  disabled={!isValid}
                />
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modales auxiliares */}
      <MapaRutaModal
        route={selectedRoute}
        visible={showModal}
        onClose={() => setShowModal(false)}
        username={username}
      />
      {selectedFuelLoads && (
        <FuelLoadModal
          fuelLoads={selectedFuelLoads}
          route={selectedRoute}
          visible={showFuelModal}
          username={username}
          onClose={() => {
            setShowFuelModal(false);
            setSelectedFuelLoads(null);
          }}
        />
      )}
    </div>
  );
};

export default UsuarioRutas;
